import { lang } from '../localization'
import { addCommaToPrice } from './numbers'

/**
 * Promotion amounts arrive as int64 MINOR units in a string, and how many of those make a
 * unit depends on the currency: IRR has none, USD has two, IQD has three.
 *
 * Ported verbatim from wbs-ng rather than reimplemented. The two apps show the same
 * discount to a booker and to the agent booking on their behalf, and two roundings of the
 * same number that disagree is a support ticket nobody can reproduce.
 */
/**
 * The one minor-unit table in this app.
 *
 * Exported because PromotionStatsPanel needs the same knowledge with a different formatter
 * (Intl, for Persian numerals) and a second copy is exactly the drift this table exists to
 * prevent — the server sends minorUnitDigits on the aggregate surfaces for the same reason.
 */
export const currencyMinorDigits: Record<string, number> = {
  IRR: 0,
  USD: 2,
  EUR: 2,
  GBP: 2,
  AED: 2,
  TRY: 2,
  CNY: 2,
  RUB: 2,
  IQD: 3
}

/**
 * Splits a minor-unit string into sign, whole and fraction for a currency.
 *
 * ONE implementation of the split arithmetic, shared by both formatters. It was written twice
 * — here and in PromotionStatsPanel — and a review pointed out that the padStart/slice
 * arithmetic is precisely where an off-by-one silently misplaces a decimal point, so having
 * two of it is having two places for that bug to live.
 *
 * The sign is stripped BEFORE padding, and that is not incidental: `'-5'.padStart(3, '0')`
 * yields `'0-5'`, because padding is inserted before the existing content INCLUDING the sign.
 * Padding a signed string corrupts the split.
 *
 * String throughout, never Number: these are int64 minor units, which is why the server sends
 * them as strings at all.
 */
export function splitMinor(
  minorAmount: string,
  fractionDigits: number
): { sign: string; whole: string; fraction: string } {
  const negative = minorAmount.startsWith('-')
  const unsigned = negative ? minorAmount.slice(1) : minorAmount
  const sign = negative ? '-' : ''

  if (fractionDigits === 0) {
    return { sign, whole: unsigned, fraction: '' }
  }

  const padded = unsigned.padStart(fractionDigits + 1, '0')
  return {
    sign,
    whole: padded.slice(0, -fractionDigits),
    fraction: padded.slice(-fractionDigits)
  }
}

/**
 * How many minor digits a currency has, and whether this app knows it.
 *
 * The boolean is the point. An unrecognised currency falls back to rendering its integer
 * as-is, which is the least-bad of three options — inventing a decimal point in the wrong
 * place is worse, dropping the unit is worst — but it is INDISTINGUISHABLE on screen from a
 * correctly-rendered zero-digit currency. So the fallback is announced to engineering rather
 * than only to nobody: a review pointed out that an operator misreading a hundredfold-
 * overstated figure should not be the first signal that this table went stale.
 *
 * Dev-only. A warning per render in production is noise that trains people to ignore it, and
 * the real fix is the server sending minorUnitDigits — which it already does on the aggregate
 * surfaces, and does not yet on PromotionStats.
 */
export function minorDigitsFor(currency: string): number {
  const digits = currencyMinorDigits[currency]
  if (digits === undefined && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      `[promotionAmount] no minor-unit digits for currency "${currency}" — ` +
        'rendering the integer unscaled. Add it to currencyMinorDigits, or consume ' +
        'minorUnitDigits from the server.'
    )
  }
  return digits ?? 0
}

export function formatPromotionMinorAmount(
  minorAmount: string,
  currency: string
): string {
  if (!/^\d+$/.test(minorAmount)) {
    return minorAmount
  }

  const fractionDigits = minorDigitsFor(currency)
  const { sign, whole, fraction } = splitMinor(minorAmount, fractionDigits)
  if (fractionDigits === 0) {
    return `${sign}${addCommaToPrice(whole)}`
  }
  return `${sign}${addCommaToPrice(whole)}.${fraction}`
}

// ─── Locale-aware money formatting ───────────────────────────────────────────────────────
//
// Extracted from PromotionStatsPanel when the Overview became the THIRD consumer, per the
// extract-at-three rule. It is deliberately NOT merged with formatPromotionMinorAmount above:
// that one renders in the booking flow with plain grouping, this one renders reporting
// figures with Persian numerals and a U+066B decimal mark. Same table, same split, different
// presentation — which is the split that has to be shared and the one that does not.

const numberFormatter = new Intl.NumberFormat(lang === 'fa' ? 'fa-IR' : 'en-GB')

// The decimal mark and the digits, taken FROM the formatter rather than hardcoded, so a
// figure is not half-localised.
const decimalSeparator = numberFormatter
  .format(1.5)
  .replace(/[\d\u0660-\u0669\u06F0-\u06F9]/g, '')

const localiseDigits = (digits: string): string =>
  digits.replace(/[0-9]/g, (d) => numberFormatter.format(Number(d)))

/**
 * Formats an int64 minor-unit string in the viewer's locale.
 *
 * `serverDigits` WINS when supplied. That is the whole point of the server sending
 * minorUnitDigits: a client that keeps its own currency table eventually disagrees with the
 * server, and the disagreement renders as a figure a hundredfold out. The local table is the
 * fallback for surfaces the server does not yet send it on — PromotionStats being one — and
 * minorDigitsFor announces in dev when even that is missing.
 *
 * BigInt for the whole part, string slicing for the split: an int64 exceeds what a JS number
 * holds exactly, which is why the server sends a string at all.
 */
export function formatMinorLocalized(
  minor: string,
  currency: string,
  serverDigits?: number | null
): string {
  try {
    const digits = serverDigits ?? minorDigitsFor(currency)
    const { sign, whole, fraction } = splitMinor(minor, digits)
    const formattedWhole = numberFormatter.format(
      BigInt(whole) as unknown as number
    )
    return digits === 0
      ? `${sign}${formattedWhole}`
      : `${sign}${formattedWhole}${decimalSeparator}${localiseDigits(fraction)}`
  } catch {
    // A malformed figure must not blank the surface — the other numbers are still true.
    return minor
  }
}

/** `1,250,000 IRR` — the amount and its unit, which always appear together. */
export function formatMinorWithCurrency(
  minor: string,
  currency: string,
  serverDigits?: number | null
): string {
  const value = formatMinorLocalized(minor, currency, serverDigits)
  return currency ? `${value} ${currency}` : value
}

/** Counts, localised. Dimensionless, so unlike money they may be summed across currencies. */
export function formatCount(value: number): string {
  return numberFormatter.format(value)
}
